import type {
  LiveAgentRecall,
  LiveAgentRecallInput,
  LiveAgentTask,
  LiveAgentTaskState,
  ProductEvent,
} from "@rakazo/contracts";
import { isRetryableError, runThreadSubscription } from "@rakazo/core";
import type { ArtifactTarget } from "./artifact-open";
import type { LiveTaskState, LiveToolBroker, LiveToolResult } from "./live-voice";
import type { LiveVoiceTiming } from "./live-voice-timing";
import { rpc } from "./rpc";

const ACTIVE_RUN_STATUSES: ReadonlySet<string> = new Set([
  "queued",
  "leased",
  "running",
  "waiting_input",
  "waiting_takeover",
]);
const ANSWER_WAIT_MS = 30 * 60_000;
// The acknowledgement is spoken when the run is first read; this is only the fallback for a
// slow first read. Later updates follow a new step (not twice within the gap) or this interval,
// which backs off while nothing changes so a long wait is not chatter.
// Підтвердження звучить після першого читання запуску; це лише запас на повільне читання. Далі
// оновлення йдуть за новим кроком (не частіше за паузу) або за цим інтервалом, що слабшає.
const FIRST_PROGRESS_MS = 6_000;
const PROGRESS_INTERVAL_MS = 15_000;
const PROGRESS_INTERVAL_MAX_MS = 60_000;
const PROGRESS_BACKOFF = 1.6;
const NARRATION_MIN_GAP_MS = 8_000;
type SendInput = ArtifactTarget & {
  text: string;
  clientNonce: string;
  voice: true;
  taskMode: "auto" | "new" | "continue";
  taskId?: string;
};
type TaskInput = ArtifactTarget & { taskId?: string; runId?: string };
type CancelInput = ArtifactTarget & { runId: string };

export type LiveToolBrokerOptions = {
  automaticInput?: boolean;
  callId?: string;
  timing?: LiveVoiceTiming;
  beforeRecall?: () => Promise<void>;
  previousCallIds?: string[];
  recall?: (
    input: ArtifactTarget & LiveAgentRecallInput,
    signal?: AbortSignal,
  ) => Promise<LiveAgentRecall>;
  send?: (input: SendInput) => Promise<{ taskId: string; runId: string }>;
  read?: (input: TaskInput, signal?: AbortSignal) => Promise<LiveAgentTaskState>;
  subscribe?: (
    target: ArtifactTarget,
    cursor: number,
    signal: AbortSignal,
  ) => Promise<AsyncIterable<ProductEvent>>;
  cancel?: (
    input: CancelInput,
    signal?: AbortSignal,
  ) => Promise<{ ok: boolean; cancelledRunIds?: string[] }>;
  answerWaitMs?: number;
};

export type LiveInputToolBroker = LiveToolBroker & {
  followInput(input: {
    taskId: string;
    runId: string;
    signal?: AbortSignal;
    onProgress?: (result: LiveToolResult) => void;
  }): Promise<LiveToolResult>;
};

function task(value: LiveAgentTask | null): LiveTaskState {
  return {
    kind: value?.terminationUnconfirmed
      ? "status"
      : value?.status === "failed"
        ? "failed"
        : value?.status === "cancelled"
          ? "cancelled"
          : value
            ? "status"
            : "idle",
    taskId: value?.taskId ?? null,
    runId: value?.runId ?? null,
    status: value?.terminationUnconfirmed ? "termination_unconfirmed" : (value?.status ?? null),
    ...(value?.activity ? { activity: value.activity } : {}),
  };
}
function result(value: LiveAgentTask): LiveToolResult {
  if (value.terminationUnconfirmed)
    return {
      response: {
        ok: false,
        found: true,
        taskId: value.taskId,
        runId: value.runId,
        status: "termination_unconfirmed",
        cancelled: false,
        terminationUnconfirmed: true,
        error:
          "Remote termination has not been confirmed. The external action may still be running.",
      },
      scheduling: "WHEN_IDLE",
      task: task(value),
    };
  const active = ACTIVE_RUN_STATUSES.has(value.status);
  const failed = value.status === "failed" || value.status === "cancelled";
  return {
    response: {
      ok: !failed,
      found: true,
      taskId: value.taskId,
      runId: value.runId,
      status: value.status,
      ...(value.answer
        ? {
            answer: value.answer,
            answerTruncated: value.answerTruncated,
            ...(value.answerMessageId ? { answerMessageId: value.answerMessageId } : {}),
          }
        : {}),
      ...(value.status === "waiting_input"
        ? { waiting: true }
        : active
          ? { pending: true, ...(value.activity ? { activity: value.activity } : {}) }
          : failed
            ? {
                error:
                  value.status === "cancelled"
                    ? "The agent request was cancelled."
                    : "The agent request failed.",
              }
            : !value.answer
              ? {
                  noSpokenAnswer: true,
                  message:
                    "The task completed without a spoken answer. Its result is available in the chat.",
                }
              : {}),
    },
    scheduling: active && value.status !== "waiting_input" ? "SILENT" : "WHEN_IDLE",
    task: task(value),
  };
}

/** Durable, scoped task reads plus the existing event journal; no repeated full-chat polling.
 * Адресне читання збереженої роботи й чинний журнал подій, без опитування всього чату. */
export function createLiveToolBroker(
  target: ArtifactTarget,
  options: LiveToolBrokerOptions = {},
): LiveInputToolBroker {
  const send = options.send ?? ((input: SendInput) => rpc.live.send(input));
  const read =
    options.read ??
    ((input: TaskInput, signal?: AbortSignal) =>
      rpc.live.task(input, signal ? { signal } : undefined));
  const subscribe =
    options.subscribe ??
    ((input: ArtifactTarget, cursor: number, signal: AbortSignal) =>
      rpc.threads.subscribe({ ...input, cursor }, { signal }));
  const cancel =
    options.cancel ??
    ((input: CancelInput, signal?: AbortSignal) =>
      rpc.live.cancel(input, signal ? { signal } : undefined));
  let lastTaskId: string | undefined;
  const recallCalls = new Map<number, string>();
  if (options.callId) recallCalls.set(0, options.callId);
  options.previousCallIds?.slice(0, 3).forEach((callId, index) => {
    recallCalls.set(index + 1, callId);
  });

  function waitForAnswer(
    taskId: string,
    runId: string,
    signal?: AbortSignal,
    onProgress?: (result: LiveToolResult) => void,
  ): Promise<LiveToolResult> {
    return new Promise((resolve) => {
      const controller = new AbortController();
      let settled = false;
      let state: LiveAgentTaskState | null = null;
      let reread: Promise<LiveAgentTaskState> | null = null;
      let dirty = false;
      let progressTimer: ReturnType<typeof setTimeout> | undefined;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      let retryDelay = 250;
      let readAt = 0;
      let accepted = false;
      let narratedAt = 0;
      let narratedLabel: string | null = null;
      let quiet = 0;
      const finish = (value: LiveToolResult) => {
        if (settled) return;
        settled = true;
        controller.abort();
        clearTimeout(deadline);
        clearTimeout(progressTimer);
        clearTimeout(retryTimer);
        signal?.removeEventListener("abort", aborted);
        resolve(value);
      };
      const failure = (error: string): LiveToolResult => ({
        response: { ok: false, error, taskId, runId, status: state?.task?.status ?? null },
        scheduling: "WHEN_IDLE",
        task: task(state?.task ?? null),
      });
      const aborted = () => finish(failure("Voice wait ended. The task has not been cancelled."));
      const deadline = setTimeout(
        () =>
          finish(
            failure(
              "The agent is still working. Its saved result can be read by task id without starting it again.",
            ),
          ),
        options.answerWaitMs ?? ANSWER_WAIT_MS,
      );
      signal?.addEventListener("abort", aborted, { once: true });
      if (signal?.aborted) {
        aborted();
        return;
      }
      const inspect = (next: LiveAgentTaskState) => {
        if (settled) return next;
        state = next;
        readAt = Date.now();
        if (!next.task) finish(failure("The agent task could not be read."));
        else if (
          !ACTIVE_RUN_STATUSES.has(next.task.status) ||
          (next.task.status === "waiting_input" && next.task.answer)
        )
          finish(result(next.task));
        else if (onProgress) {
          const label = next.task.activity?.label ?? null;
          if (!accepted) {
            accepted = true;
            narrate("accepted");
          } else if (
            label !== null &&
            label !== narratedLabel &&
            Date.now() - narratedAt >= NARRATION_MIN_GAP_MS
          ) {
            quiet = 0;
            narrate("progress");
          }
        }
        return next;
      };
      // The elapsed time was true when the task was read; add the time since then.
      // Час був точним на момент читання завдання; додаємо час, що минув відтоді.
      const snapshot = (phase: "accepted" | "progress"): LiveToolResult | null => {
        const current = state?.task;
        if (!current || !ACTIVE_RUN_STATUSES.has(current.status)) return null;
        const sinceRead = Math.floor((Date.now() - readAt) / 1000);
        const activity = current.activity && {
          ...current.activity,
          elapsedSeconds:
            current.activity.elapsedSeconds === null
              ? null
              : current.activity.elapsedSeconds + sinceRead,
        };
        return {
          response: {
            ok: true,
            pending: true,
            taskId,
            runId,
            status: current.status,
            phase,
            ...(activity ? { activity } : {}),
          },
          // Receipt and ordinary work are silent context. / Прийняття й робота — тихий контекст.
          scheduling: "SILENT",
          task: task(activity ? { ...current, activity } : current),
        };
      };
      const scheduleProgress = () => {
        clearTimeout(progressTimer);
        const delay = Math.round(
          Math.min(PROGRESS_INTERVAL_MS * PROGRESS_BACKOFF ** quiet, PROGRESS_INTERVAL_MAX_MS),
        );
        progressTimer = setTimeout(progress, delay);
      };
      const narrate = (phase: "accepted" | "progress"): boolean => {
        const update = snapshot(phase);
        if (!update) return false;
        onProgress?.(update);
        narratedAt = Date.now();
        narratedLabel = state?.task?.activity?.label ?? null;
        scheduleProgress();
        return true;
      };
      const refresh = () => {
        dirty = true;
        if (reread) return reread;
        reread = (async () => {
          do {
            dirty = false;
            const next = await read({ ...target, taskId, runId }, controller.signal);
            inspect(next);
          } while (dirty && !settled);
          return state!;
        })().finally(() => {
          reread = null;
        });
        return reread;
      };
      const progress = () => {
        if (settled) return;
        quiet += 1;
        if (!narrate("progress")) scheduleProgress();
      };
      const refreshForEvent = () => {
        if (settled) return;
        void refresh()
          .then(() => {
            retryDelay = 250;
          })
          .catch((error: unknown) => {
            if (!isRetryableError(error)) finish(failure("The agent answer could not be read."));
            else if (!settled && retryTimer === undefined) {
              retryTimer = setTimeout(() => {
                retryTimer = undefined;
                refreshForEvent();
              }, retryDelay);
              retryDelay = Math.min(retryDelay * 2, 5_000);
            }
          });
      };
      if (onProgress) progressTimer = setTimeout(progress, FIRST_PROGRESS_MS);
      void runThreadSubscription({
        signal: controller.signal,
        loadInitial: refresh,
        loadHead: refresh,
        refresh,
        currentSnapshot: () => state,
        subscribe: (cursor, abort) => subscribe(target, cursor, abort),
        applyEvent: (event) => {
          if (state && event.threadId === state.threadId)
            state = { ...state, cursor: Math.max(state.cursor, event.seq) };
        },
        onEvent: (event) => {
          if (
            event.type === "thread.cleared" ||
            (event.runId === runId && event.type !== "thread.progress")
          )
            refreshForEvent();
        },
        onFailure: (error) => {
          if (!isRetryableError(error)) finish(failure("The agent answer could not be read."));
        },
      }).catch(() => finish(failure("The agent answer could not be read.")));
    });
  }

  return {
    followInput(input) {
      lastTaskId = input.taskId;
      return waitForAnswer(input.taskId, input.runId, input.signal, input.onProgress);
    },
    async recallConversation(input) {
      if (!options.callId)
        throw new Error("Voice call identity is missing / Немає ідентифікатора розмови");
      input.signal?.throwIfAborted();
      await options.beforeRecall?.();
      input.signal?.throwIfAborted();
      const conversation = input.conversation ?? 0;
      const callId = input.callId ?? recallCalls.get(conversation);
      if (input.beforeSeq !== undefined && !callId)
        throw new Error(
          "Voice paging requires callId / Сторінки потребують ідентифікатора розмови",
        );
      const recall =
        options.recall ??
        ((data, signal) => rpc.live.recall(data, signal ? { signal } : undefined));
      const memory = await recall(
        {
          ...target,
          currentCallId: options.callId,
          conversation,
          ...(callId ? { callId } : {}),
          ...(input.beforeSeq === undefined ? {} : { beforeSeq: input.beforeSeq }),
        },
        input.signal,
      );
      if (memory.callId) recallCalls.set(conversation, memory.callId);
      return {
        response: { ok: true, ...memory, quotedHistory: true, authorizesActions: false },
        scheduling: "WHEN_IDLE",
      };
    },
    async askAssistant(input) {
      if (options.automaticInput)
        return {
          response: {
            ok: true,
            pending: true,
            inputDelivery: "automatic",
            authorizesActions: false,
            message:
              "Fresh spoken input is delivered independently. This tool does not submit work or confirm acceptance. Wait for the actual server receipt and agent result updates; never repeat the request.",
          },
          scheduling: "SILENT",
        };
      if (input.signal?.aborted)
        return { response: { ok: false, error: "Voice wait ended." }, scheduling: "SILENT" };
      const text = input.text.trim();
      if (!text)
        return {
          response: { ok: false, error: "No question or request was provided." },
          scheduling: "WHEN_IDLE",
        };
      const operation = options.timing?.beginTask();
      let sent: { taskId: string; runId: string };
      try {
        let clientNonce = input.idempotencyKey;
        if (options.callId) {
          if (!/^[A-Za-z0-9_-]{1,96}$/.test(options.callId))
            throw Error("Invalid voice call identity / Некоректна назва голосової розмови");
          const digest = await crypto.subtle.digest(
            "SHA-256",
            new TextEncoder().encode(input.idempotencyKey),
          );
          const suffix = Array.from(new Uint8Array(digest), (byte) =>
            byte.toString(16).padStart(2, "0"),
          ).join("");
          clientNonce = `call:${options.callId}:tool-${suffix}`;
        }
        // Once admitted, submission outlives the voice wait, including nonce preparation.
        // Прийнята передача переживає закриття голосу, зокрема підготовку ключа повтору.
        sent = await send({
          ...target,
          text,
          clientNonce,
          voice: true,
          taskMode: input.mode ?? "auto",
          ...(input.taskId ? { taskId: input.taskId } : {}),
        });
        options.timing?.mark("tool_accepted", operation);
        lastTaskId = sent.taskId;
      } catch {
        options.timing?.mark("tool_result", operation);
        return {
          response: { ok: false, error: "The agent request could not be sent." },
          scheduling: "WHEN_IDLE",
        };
      }
      const answer = await waitForAnswer(sent.taskId, sent.runId, input.signal, input.onProgress);
      options.timing?.mark("tool_result", operation);
      return answer;
    },
    async getTaskStatus(input) {
      try {
        const requested = input.taskId?.trim() || lastTaskId;
        if (options.automaticInput && !requested)
          return {
            response: { ok: true, found: false, status: "idle" },
            scheduling: "SILENT",
            task: task(null),
          };
        const value = await read(
          { ...target, ...(requested ? { taskId: requested } : {}) },
          input.signal,
        );
        if (!value.task)
          return {
            response: { ok: true, found: false, status: "idle" },
            scheduling: "SILENT",
            task: task(null),
          };
        return result(value.task);
      } catch {
        return {
          response: { ok: false, error: "The agent status could not be read." },
          scheduling: "SILENT",
        };
      }
    },
    async cancelTask(input) {
      try {
        const requested = input.taskId?.trim() || lastTaskId;
        if (options.automaticInput && !requested)
          return {
            response: { ok: true, cancelled: false, status: "idle" },
            scheduling: "SILENT",
            task: task(null),
          };
        const value = await read(
          { ...target, ...(requested ? { taskId: requested } : {}) },
          input.signal,
        );
        const selected = value.task;
        if (!selected || !ACTIVE_RUN_STATUSES.has(selected.status))
          return {
            response: {
              ok: true,
              cancelled: false,
              status: selected?.status ?? "idle",
              message: "There is no running agent task to cancel.",
            },
            scheduling: "WHEN_IDLE",
            task: task(selected),
          };
        if (input.signal?.aborted)
          return {
            response: { ok: false, cancelled: false, status: selected.status },
            scheduling: "SILENT",
            task: task(selected),
          };
        const stopped = await cancel({ ...target, runId: selected.runId }, input.signal);
        const confirmed = stopped.cancelledRunIds?.includes(selected.runId) === true;
        if (confirmed)
          return {
            response: {
              ok: true,
              cancelled: true,
              taskId: selected.taskId,
              runId: selected.runId,
              status: "cancelled",
            },
            scheduling: "WHEN_IDLE",
            task: task({ ...selected, status: "cancelled" }),
          };
        const current = (await read({ ...target, runId: selected.runId })).task;
        return {
          response: {
            ok: false,
            cancelled: false,
            taskId: selected.taskId,
            runId: selected.runId,
            status: current?.status ?? selected.status,
            message: "The task was not cancelled. It may have already finished.",
          },
          scheduling: "WHEN_IDLE",
          task: task(current ?? selected),
        };
      } catch {
        return {
          response: {
            ok: false,
            cancelled: false,
            message: "The agent task could not be cancelled. It may still be running.",
          },
          scheduling: "WHEN_IDLE",
        };
      }
    },
  };
}
